import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Check, ChevronsUpDown, LogOutIcon, LucideAngularModule, Menu } from 'lucide-angular';
import { catchError, of } from 'rxjs';
import { UserService } from '../../../services/user/user.service';
import { AuthService } from '../../../services/auth/auth.service';
import { PortalService } from '../../../services/portal/portal.service';
import { Click } from '../../../directives/click/click';
import { InitialsPipe } from '../../../pipes/initials/initials-pipe';
import { OrganizationService } from '../../../services/organization/organization.service';
import { SidebarService } from '../../../services/sidebar/sidebar.service';
import { membershipRolesI18n } from '../../../enums/membership-role.enum';
import { LowerCasePipe } from '@angular/common';
import { isSubscriptionActive } from '../../../utils/is-subscription-active.util';

export type TopbarContext = 'producer' | 'portal';

// The single app topbar, shared by the producer app and the customer portal
// (context input). Both contexts render the same organization block, user
// credentials and logout; the portal adds the brand (no sidebar there) and
// drops the hamburger. Dual-context users get the binary mode switcher.
@Component({
  selector: 'skk-topbar',
  imports: [LucideAngularModule, Click, InitialsPipe, LowerCasePipe],
  templateUrl: './topbar.html',
  styleUrl: './topbar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Topbar {
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly organizationService = inject(OrganizationService);
  private readonly sidebarService = inject(SidebarService);
  private readonly portalService = inject(PortalService);
  private readonly router = inject(Router);

  readonly LogOutIcon = LogOutIcon;
  readonly MenuIcon = Menu;
  readonly SwitchIcon = ChevronsUpDown;
  readonly CheckIcon = Check;

  readonly context = input<TopbarContext>('producer');
  readonly isPortal = computed(() => this.context() === 'portal');

  // The organization signal may not be populated in the portal context (no
  // resolver ran there) - fall back to the cache-backed fetch.
  private readonly fetchedOrganization = toSignal(
    this.organizationService.getCurrentOrganization().pipe(catchError(() => of(null))),
    { initialValue: null },
  );
  readonly currentOrganization = computed(
    () => this.organizationService.organization() ?? this.fetchedOrganization(),
  );
  readonly currentRole = computed(() => {
    const org = this.currentOrganization();
    if (!org) return '';
    // Prefer the assigned organization role name (e.g. "Sales") - members want
    // to see what they actually are, not the generic membership tier. Falls
    // back to Owner/Member when no role is assigned.
    return org.organizationRoleName || membershipRolesI18n.get(org.role) || '';
  });
  readonly currentUser = this.userService.user;

  // The binary mode switcher (producer app / customer portal) shows only for
  // dual-context users: producers with portal relations, or portal buyers whose
  // organization also runs a live producer subscription.
  private readonly portalSuppliers = toSignal(
    this.portalService.getSuppliers().pipe(catchError(() => of([]))),
    { initialValue: [] },
  );
  readonly portalAvailable = computed(() => this.portalSuppliers().length > 0);
  readonly producerAvailable = computed(() => isSubscriptionActive(this.currentOrganization()));
  readonly switcherAvailable = computed(() =>
    this.isPortal() ? this.producerAvailable() : this.portalAvailable(),
  );
  readonly modeMenuOpen = signal(false);

  toggleModeMenu(): void {
    this.modeMenuOpen.update((open) => !open);
  }

  switchMode(target: TopbarContext): void {
    this.modeMenuOpen.set(false);
    if (target !== this.context()) {
      void this.router.navigate([target === 'portal' ? '/portal' : '/']);
    }
  }

  toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  logout(): void {
    this.authService.logout().subscribe();
  }
}
